import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  role: string | null;
}

const UserVerification = () => {
  const { toast } = useToast();
  const { data: pendingUsers, refetch } = useQuery({
    queryKey: ['pending-verifications'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .is('role', null);
      if (error) throw error;
      return data as Profile[];
    }
  });

  const handleVerification = async (userId: string, approved: boolean) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: approved ? 'verified' : 'rejected' })
        .eq('id', userId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `User ${approved ? 'verified' : 'rejected'} successfully`,
      });

      refetch();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update verification status",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4">
      {pendingUsers?.map((user) => (
        <Card key={user.id} className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">{user.first_name} {user.last_name}</h3>
              <Badge variant="outline" className="mt-2">Pending Verification</Badge>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                className="text-green-600"
                onClick={() => handleVerification(user.id, true)}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve
              </Button>
              <Button
                variant="outline"
                className="text-red-600"
                onClick={() => handleVerification(user.id, false)}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </div>
          </div>
        </Card>
      ))}
      {(!pendingUsers || pendingUsers.length === 0) && (
        <Card className="p-6">
          <p className="text-center text-muted-foreground">No pending verifications</p>
        </Card>
      )}
    </div>
  );
};

export default UserVerification;